import { useState, useEffect, useCallback } from 'react';
import { knowledgeAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { HardDrive, RefreshCw, Loader2, Clock, AlertCircle } from 'lucide-react';
import { fechaYHora } from '../../lib/fechas';

export default function SourceConfigPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const res = await knowledgeAPI.getSourceConfig();
      setConfig(res.data);
    } catch {
      toast.error('Error cargando config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await knowledgeAPI.updateSourceConfig({
        source_path: config.source_path || '',
        enabled: config.enabled || false,
        scan_interval_minutes: config.scan_interval_minutes || 10,
      });
      toast.success('Configuracion guardada');
    } catch {
      toast.error('Error guardando');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-[120px]" />;

  return (
    <Card className="border border-brand-border rounded-sm" data-testid="source-config-panel">
      <CardHeader className="pb-2 pt-4 px-5">
        <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-1.5">
          <HardDrive className="w-3.5 h-3.5" /> Volumen /data/sources
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5 space-y-4">
        <p className="text-[11px] text-zinc-500">
          El volumen montado en <span className="font-mono">/data/sources</span> es de solo lectura.
          El auto-scan ya no indexa todo el arbol: solo reindexa los <b>mounts declarados</b> abajo.
          Configure intervalo y habilite el scheduler aqui.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Ruta raiz (solo lectura)</Label>
            <Input
              data-testid="source-path-input"
              value={config?.source_path || '/data/sources'}
              onChange={(e) => setConfig((p) => ({ ...p, source_path: e.target.value }))}
              className="rounded-sm text-xs font-mono h-9"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Intervalo scan mounts (min)</Label>
            <Input
              type="number"
              min={1}
              value={config?.scan_interval_minutes || 10}
              onChange={(e) => setConfig((p) => ({ ...p, scan_interval_minutes: parseInt(e.target.value, 10) || 10 }))}
              className="rounded-sm text-xs h-9"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={config?.enabled || false}
            onCheckedChange={(v) => setConfig((p) => ({ ...p, enabled: v }))}
          />
          <Label className="text-xs text-zinc-600">Scheduler de reindex de mounts habilitado</Label>
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm" className="text-xs h-8">
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Guardar
        </Button>
        {config?.last_scan && (
          <div className="flex items-center gap-1.5 text-[10px] text-zinc-400">
            <Clock className="w-3 h-3" />
            Ultimo ciclo: {fechaYHora(config.last_scan)}
          </div>
        )}
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-sm">
          <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-amber-700">
            Organice el contenido en subcarpetas (ej. <span className="font-mono">capsula_sistemas/</span>, <span className="font-mono">capsula_compras/</span>)
            y declare cada una como mount. Luego asigne mounts a capsulas en Configuracion de Capsula.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
